export function debounce(fn, wait = 300) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

export function rollText(element, text, direction) {
  const previous = (element.lastElementChild ?? element).textContent.trim();
  if (!matchMedia('(prefers-reduced-motion: no-preference)').matches) {
    element.textContent = text;
    return;
  }

  const outgoing = document.createElement('span');
  const incoming = document.createElement('span');
  outgoing.textContent = previous;
  outgoing.setAttribute('aria-hidden', 'true');
  incoming.textContent = text;
  element.replaceChildren(outgoing, incoming);

  const motion = getComputedStyle(element);
  const timing = {
    duration: parseFloat(motion.getPropertyValue('--duration-normal')),
    easing: motion.getPropertyValue('--ease-out'),
  };
  element.animate({ clipPath: ['inset(0)', 'inset(0)'] }, timing);
  outgoing.animate(
    [{ translate: '0 0', opacity: 1 }, { translate: `0 ${-100 * direction}%`, opacity: 0 }],
    { ...timing, fill: 'forwards' },
  ).onfinish = () => outgoing.remove();
  incoming.animate([{ translate: `0 ${100 * direction}%`, opacity: 0 }, { translate: '0 0', opacity: 1 }], timing);
}

export function shake(element) {
  if (!matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
  const motion = getComputedStyle(element);
  element.animate([0, -4, 4, -2, 1, 0].map((x) => ({ translate: `${x}px 0` })), {
    duration: parseFloat(motion.getPropertyValue('--duration-normal')),
    easing: motion.getPropertyValue('--ease-out'),
  });
}

export function activateScripts(root, selector = 'script') {
  const scripts = [...root.querySelectorAll(selector)];
  if (root instanceof HTMLScriptElement && root.matches(selector)) scripts.push(root);
  for (const inert of scripts) {
    const script = document.createElement('script');
    for (const { name, value } of inert.attributes) script.setAttribute(name, value);
    script.textContent = inert.textContent;
    inert.replaceWith(script);
  }
}

const SECTION_STYLESHEET = 'style[data-section-stylesheet]';

export function parseSectionHTML(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const adopted = new Set([...document.head.querySelectorAll(SECTION_STYLESHEET)].map((style) => style.textContent));
  for (const style of doc.querySelectorAll(SECTION_STYLESHEET)) {
    if (adopted.has(style.textContent)) {
      style.remove();
      continue;
    }
    adopted.add(style.textContent);
    document.head.append(style);
  }
  return doc;
}

export async function fetchTextWithTimeout(input, { timeout = 8000, signal, ...init } = {}) {
  const controller = new AbortController();
  const timeoutMs = Number.isFinite(timeout) && timeout >= 0 ? timeout : 8000;
  let abortReason;
  const abortRequest = (reason) => {
    abortReason = reason;
    controller.abort(reason);
  };
  const abortFromCaller = () => abortRequest(signal.reason || abortError());

  if (signal?.aborted) {
    abortFromCaller();
  } else {
    signal?.addEventListener('abort', abortFromCaller, { once: true });
  }

  const timeoutId = setTimeout(() => {
    abortRequest(timeoutError());
  }, timeoutMs);

  try {
    const response = await fetch(input, { ...init, signal: controller.signal });
    const text = await response.text();
    return { response, text };
  } catch (error) {
    throw abortReason || error;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}

export function getCartSections() {
  const sections = [];
  const el = document.querySelector('scale-cart-drawer, scale-cart-page');
  if (el?.dataset.sectionId) sections.push(el.dataset.sectionId);
  return sections;
}

function abortError() {
  return new DOMException('The request was aborted.', 'AbortError');
}

function timeoutError() {
  return new DOMException('The request timed out.', 'TimeoutError');
}


const DISPATCH_DAYS = Object.freeze({
  weekdays: [1, 2, 3, 4, 5],
  monday_to_saturday: [1, 2, 3, 4, 5, 6],
  every_day: [0, 1, 2, 3, 4, 5, 6],
});

const RECENTLY_VIEWED_KEY = 'scale_recently_viewed';

export function readRecentlyViewed() {
  try {
    return JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY)) || [];
  } catch {
    return [];
  }
}

export function recordRecentlyViewed(handle) {
  const viewed = [handle, ...readRecentlyViewed().filter((entry) => entry !== handle)].slice(0, 20);
  try {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(viewed));
  } catch {   }
}

export function parseUtcOffset(value) {
  const match = /^([+-])(\d{2}):?(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

export function parseUtcOffsets(value) {
  const offsets = String(value ?? '').split(',').map(parseUtcOffset);
  return offsets.length && offsets.every((offset) => offset !== null) ? offsets : null;
}

export function parseClockTime(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function parseWallClockDate(value) {
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const [, year, month, day, hours, minutes] = match.map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hours > 23 || minutes > 59) return null;
  const wall = new Date(Date.UTC(year, month - 1, day, hours, minutes));
  if (wall.getUTCMonth() !== month - 1 || wall.getUTCDate() !== day) return null;
  return wall;
}

export function shopWallClock(offsetMinutes, now = Date.now()) {
  return new Date(now + offsetMinutes * 60000);
}

export function wallClockToEpoch(wall, offsetMinutes) {
  return wall.getTime() - offsetMinutes * 60000;
}

export function nextDispatchCutoff({ cutoffMinutes, dispatchDays, utcOffsets, now = Date.now() }) {
  const days = DISPATCH_DAYS[dispatchDays];
  if (!days || cutoffMinutes === null || !utcOffsets?.length) return null;

  const wallNow = shopWallClock(utcOffsets[0], now);
  for (let ahead = 0; ahead <= 7; ahead++) {
    const wall = new Date(Date.UTC(
      wallNow.getUTCFullYear(),
      wallNow.getUTCMonth(),
      wallNow.getUTCDate() + ahead,
      Math.floor(cutoffMinutes / 60),
      cutoffMinutes % 60
    ));
    const offsetMinutes = utcOffsets[Math.min(ahead, utcOffsets.length - 1)];
    const epoch = wallClockToEpoch(wall, offsetMinutes);
    if (!days.includes(wall.getUTCDay()) || epoch <= now) continue;
    return { epoch, wall, daysAhead: ahead };
  }
  return null;
}
